import type {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { IconButton } from '@shared/components/iconButton/IconButton';
import styles from './RestaurantEventForm.scss';
import type {
  BookingUnit,
  GuestEmailRequirement,
  RestaurantEvent,
  RestaurantEventImage,
  RestaurantMetadata,
} from '@shared/types/restaurants';
import { centsToDecimal } from '@shared/utils/currency';
import { formatPercentNumber } from '@shared/utils/formatPercent';

export const EVENT_STEPS = [
  { id: 'event-information', label: 'Event Information' },
  { id: 'guest-capacity', label: 'Guest Capacity' },
  { id: 'event-photos', label: 'Event Photos' },
  { id: 'event-add-ons', label: 'Event Add-Ons (optional)' },
  { id: 'event-disclaimers', label: 'Event Disclaimers' },
  { id: 'event-confirmation', label: 'Event Confirmation' },
] as const;

export interface TicketTierFormItem {
  name: string;
  description: string;
  price: string;
  capacity: string;
  tableCount: string;
  floorPlanTableIds?: string[];
}

export interface ServiceFeeFormItem {
  label: string;
  percentage: number | '';
}

const DEFAULT_TIER: TicketTierFormItem = {
  name: 'General Admission',
  description: '',
  price: '',
  capacity: '',
  tableCount: '0',
  floorPlanTableIds: [],
};

export interface EventDisclaimerFormItem {
  title: string;
  description: string;
}

export const DEFAULT_EMPTY_DISCLAIMER: EventDisclaimerFormItem = {
  title: '',
  description: '',
};

/** True when the row has no user input (placeholder / unused row). */
export function isBlankDisclaimerRow(
  disclaimer: EventDisclaimerFormItem | undefined,
): boolean {
  if (!disclaimer) return true;
  return (
    String(disclaimer.title ?? '').trim() === '' &&
    String(disclaimer.description ?? '').trim() === ''
  );
}

export function isPersistableDisclaimerFormItem(
  disclaimer: EventDisclaimerFormItem | undefined,
): boolean {
  return Boolean(disclaimer && String(disclaimer.title ?? '').trim() !== '');
}

export interface EventAddOnFormItem {
  id?: string;
  name: string;
  description: string;
  price: string;
  imageKey: string;
  tierIds: string[];
}

export const DEFAULT_EMPTY_ADD_ON: EventAddOnFormItem = {
  name: '',
  description: '',
  price: '',
  imageKey: '',
  tierIds: [],
};

/** True when the row has no user input and is not a saved add-on (no id). */
export function isBlankAddOnRow(
  addOn: EventAddOnFormItem | undefined,
): boolean {
  if (!addOn) return true;
  if (addOn.id) return false;
  return (
    String(addOn.name ?? '').trim() === '' &&
    String(addOn.imageKey ?? '').trim() === '' &&
    String(addOn.price ?? '').trim() === '' &&
    String(addOn.description ?? '').trim() === '' &&
    (addOn.tierIds?.length ?? 0) === 0
  );
}

/** Complete add-on row: required for save / preview (matches Done button). */
export function isPersistableAddOnFormItem(addOn: EventAddOnFormItem): boolean {
  if (
    !String(addOn.name ?? '').trim() ||
    !String(addOn.imageKey ?? '').trim()
  ) {
    return false;
  }
  const rawPrice = addOn.price;
  if (rawPrice === '' || rawPrice === null || rawPrice === undefined) {
    return false;
  }
  const priceString = String(rawPrice).trim();
  if (priceString === '') return false;
  return Number.isFinite(Number.parseFloat(priceString));
}

export type ImageData = Pick<RestaurantEventImage, 'imageKey'>;

export interface EventFormData {
  name: string;
  description: string;
  startDate: string;
  startTime: string;
  endTime: string;
  capacity: string;
  location: string;
  directions: string;
  customConfirmationMessage: string;
  includeTicketsLinkInConfirmation: boolean;
  guestEmailRequirement: GuestEmailRequirement;
  bookingUnit: BookingUnit;
  serviceChargePercentage?: number | '';
  serviceFees: ServiceFeeFormItem[];
  ticketTiers: TicketTierFormItem[];
  addOns: EventAddOnFormItem[];
  images: ImageData[];
  disclaimers: EventDisclaimerFormItem[];
}

/** Top-level form keys per step (order matches `EVENT_STEPS`) for validation error navigation. */
const EVENT_STEP_FORM_FIELDS: Record<
  (typeof EVENT_STEPS)[number]['id'],
  (keyof EventFormData)[]
> = {
  'event-information': [
    'name',
    'description',
    'startDate',
    'startTime',
    'endTime',
    'location',
    'directions',
    'serviceChargePercentage',
    'serviceFees',
  ],
  'guest-capacity': ['bookingUnit', 'capacity', 'ticketTiers'],
  'event-photos': ['images'],
  'event-add-ons': ['addOns'],
  'event-disclaimers': ['disclaimers'],
  'event-confirmation': [
    'customConfirmationMessage',
    'includeTicketsLinkInConfirmation',
    'guestEmailRequirement',
  ],
};

function hasNestedFieldError(node: unknown): boolean {
  if (node == null) return false;
  if (typeof node !== 'object') return false;
  const rec = node as Record<string, unknown>;
  if (typeof rec.message === 'string' && rec.message.length > 0) {
    return true;
  }
  if (Array.isArray(node)) {
    return node.some((item) => hasNestedFieldError(item));
  }
  return Object.values(rec).some((value) => hasNestedFieldError(value));
}

/** First step in flow order that contains a validation error (for Save & Exit / submit failures). */
export function getFirstEventFormStepIdWithErrors(
  errors: FieldErrors<EventFormData>,
): (typeof EVENT_STEPS)[number]['id'] | null {
  for (const step of EVENT_STEPS) {
    for (const key of EVENT_STEP_FORM_FIELDS[step.id]) {
      if (hasNestedFieldError(errors[key])) {
        return step.id;
      }
    }
  }
  return null;
}

export function getDefaultEventFormValues(
  restaurantEvent: RestaurantEvent | null | undefined,
  restaurant?: RestaurantMetadata,
): EventFormData {
  const serviceFeesFromEvent = restaurantEvent?.serviceFees;
  const hasServiceFees = (serviceFeesFromEvent?.length ?? 0) > 0;

  const tiersFromEvent = restaurantEvent?.tiers;
  const hasTiers = (tiersFromEvent?.length ?? 0) > 0;

  const disclaimersFromEvent = restaurantEvent?.disclaimers;
  const hasDisclaimers = (disclaimersFromEvent?.length ?? 0) > 0;

  return {
    name: restaurantEvent?.name ?? '',
    description: restaurantEvent?.description ?? '',
    startDate: restaurantEvent?.shift.startDate ?? '',
    startTime: restaurantEvent?.shift.startTime ?? '',
    endTime: restaurantEvent?.shift.endTime ?? '',
    capacity: restaurantEvent?.capacity ? String(restaurantEvent.capacity) : '',
    location: restaurantEvent?.location ?? '',
    directions: restaurantEvent?.directions ?? '',
    customConfirmationMessage: restaurantEvent?.customConfirmationMessage ?? '',
    includeTicketsLinkInConfirmation:
      restaurantEvent?.includeTicketsLinkInConfirmation ?? true,
    guestEmailRequirement: restaurantEvent?.guestEmailRequirement ?? 'hidden',
    bookingUnit: restaurantEvent?.bookingUnit ?? 'seat',
    serviceChargePercentage: formatPercentNumber(
      restaurantEvent?.serviceChargePercentage ?? 0,
      { decimals: 2 },
    ),
    serviceFees:
      hasServiceFees && serviceFeesFromEvent
        ? serviceFeesFromEvent.map((fee) => ({
            label: fee.label,
            percentage: formatPercentNumber(fee.percentage, { decimals: 2 }),
          }))
        : [
            {
              label: 'Service fee',
              percentage: formatPercentNumber(
                restaurantEvent?.serviceChargePercentage ?? 0,
                { decimals: 2 },
              ),
            },
          ],
    ticketTiers:
      hasTiers && tiersFromEvent
        ? tiersFromEvent.map((tier) => ({
            name: tier.name,
            description: tier.description ?? '',
            price: String(centsToDecimal(tier.price)),
            capacity: String(tier.capacity),
            floorPlanTableIds: tier.floorPlanTableIds,
            tableCount: String(tier.floorPlanTableIds.length),
          }))
        : [{ ...DEFAULT_TIER }],
    addOns:
      restaurantEvent?.addOns?.length && restaurantEvent.addOns.length > 0
        ? restaurantEvent.addOns.map((addOn) => ({
            id: addOn.id,
            name: addOn.name,
            description: addOn.description,
            price: String(centsToDecimal(addOn.price)),
            imageKey: addOn.imageKey ?? '',
            tierIds: addOn.tiers?.map((tier) => tier.id) ?? [],
          }))
        : [{ ...DEFAULT_EMPTY_ADD_ON }],
    images:
      restaurantEvent?.images?.map((image) => ({ imageKey: image.imageKey })) ??
      [],
    disclaimers:
      hasDisclaimers && disclaimersFromEvent
        ? disclaimersFromEvent.map((disclaimer) => ({
            title: disclaimer.title,
            description: disclaimer.description ?? '',
          }))
        : [{ ...DEFAULT_EMPTY_DISCLAIMER }],
  };
}

export type RestaurantEventFormProps = {
  control: Control<EventFormData>;
  setSectionRef: (id: string) => (el: HTMLElement | null) => void;
  restaurantEvent?: RestaurantEvent | null;
  getValues: UseFormGetValues<EventFormData>;
  setValue: UseFormSetValue<EventFormData>;
  watch: UseFormWatch<EventFormData>;
  handleSubmit: UseFormHandleSubmit<EventFormData>;
  onSubmit?: (data: EventFormData) => void;
  /** Only passed (and only rendered) when editing an existing event. */
  onCopyEventUrl?: () => void;
};

import { EventFormStepContent } from './EventFormStepContent';

export const RestaurantEventForm = ({
  control,
  setSectionRef,
  restaurantEvent,
  getValues,
  setValue,
  watch,
  handleSubmit: formHandleSubmit,
  onSubmit,
  onCopyEventUrl,
}: RestaurantEventFormProps) => {
  return (
    <form
      onSubmit={formHandleSubmit(onSubmit ?? (() => {}))}
      noValidate
      aria-label="Restaurant event form"
      className={styles.eventForm}
    >
      {EVENT_STEPS.map((step) => (
        <section
          key={step.id}
          ref={setSectionRef(step.id)}
          data-step-id={step.id}
          className={styles.stepContent}
        >
          {step.id !== 'event-disclaimers' && step.id !== 'event-add-ons' && (
            <div className={styles.formTitleRow}>
              <h2 className={styles.formTitle}>{step.label}</h2>
              {step.id === 'event-information' && onCopyEventUrl && (
                <IconButton
                  ariaLabel="Copy Event URL"
                  className={styles.copyEventUrlButton}
                  iconName="copy"
                  showLabel
                  onClick={onCopyEventUrl}
                />
              )}
            </div>
          )}
          <EventFormStepContent
            stepId={step.id}
            control={control}
            restaurantEvent={restaurantEvent}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
          />
        </section>
      ))}
    </form>
  );
};
